import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { IMPRESSION_TYPE } from './useRetainerRequestForm';

/**
 * Custom hook to set selected tooth from response based on the impression type.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {IMPRESSION_TYPE} params.impressionType - The type of impression (UPPER, LOWER, UPPER_AND_LOWER).
 * @param {Object} params.baseTeethSelections - An object containing methods to set the selected teeth for bite ramp and pontics in the base case.
 * @param {Function} params.baseTeethSelections.setToothForBiteRamp - Function to set the selected teeth for bite ramp in the base case.
 * @param {Function} params.baseTeethSelections.setToothForPontics - Function to set the selected teeth for pontics in the base case.
 * @param {Object} params.upperAndLowerTeethSelections - An object containing methods to set the selected teeth for bite ramp and pontics in the upper and lower case.
 * @param {Function} params.upperAndLowerTeethSelections.setUpperToothForBiteRamp - Function to set the selected teeth for bite ramp in the upper case.
 * @param {Function} params.upperAndLowerTeethSelections.setUpperToothForPontics - Function to set the selected teeth for pontics in the upper case.
 * @param {Function} params.upperAndLowerTeethSelections.setLowerToothForBiteRamp - Function to set the selected teeth for bite ramp in the lower case.
 * @param {Function} params.upperAndLowerTeethSelections.setLowerToothForPontics - Function to set the selected teeth for pontics in the lower case.
 */

const useSetSelectedToothFromResponse = ({ impressionType, baseTeethSelections, upperAndLowerTeethSelections }) => {
    const diffRetainer = useSelector((state) => state.diffRetainer);
    useEffect(() => {
        if (!impressionType) return;
        switch (impressionType) {
            case IMPRESSION_TYPE.UPPER:
            case IMPRESSION_TYPE.LOWER:
                const biteRamp = diffRetainer?.toothBiteRamp || [];
                const pontics = diffRetainer?.toothPontics || [];
                baseTeethSelections.setToothForBiteRamp(biteRamp);
                baseTeethSelections.setToothForPontics(pontics);
                break;
            case IMPRESSION_TYPE.UPPER_AND_LOWER:
                const upperBiteRamp = diffRetainer?.upperToothBiteRamp || [];
                const upperPontics = diffRetainer?.upperToothPontics || [];
                const lowerBiteRamp = diffRetainer?.lowerToothBiteRamp || [];
                const lowerPontics = diffRetainer?.lowerToothPontics || [];
                upperAndLowerTeethSelections.setUpperToothForBiteRamp(upperBiteRamp);
                upperAndLowerTeethSelections.setUpperToothForPontics(upperPontics);
                upperAndLowerTeethSelections.setLowerToothForBiteRamp(lowerBiteRamp);
                upperAndLowerTeethSelections.setLowerToothForPontics(lowerPontics);
                break;
            default:
                break;
        }
    }, [diffRetainer, impressionType]);
};

export default useSetSelectedToothFromResponse;
